import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Amenities.module.css";
import placeholderImg from "../../../assets/images/about-bg.png";
import gymImg from "../../../assets/images/gym.jpg";
import poolImg from "../../../assets/images/pool.png";
import clubhouseImg from "../../../assets/images/clubhouse.jpg";
import gameImg from "../../../assets/images/game.png";
import api from "../../../api/axios";
import { Sparkles, XCircle, MapPin, Waves, Leaf, Users, ArrowRight, Dumbbell, Gamepad2, Home as HomeIcon } from "lucide-react";

const getAmenityImage = (name) => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes("gym") || lowerName.includes("fitness")) return gymImg;
  if (lowerName.includes("pool") || lowerName.includes("swim")) return poolImg;
  if (lowerName.includes("clubhouse") || lowerName.includes("club")) return clubhouseImg;
  if (lowerName.includes("game") || lowerName.includes("indoor")) return gameImg;
  return placeholderImg;
};
const getCategoryText = (name) => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes("gym") || lowerName.includes("fitness")) return "Fitness";
  if (lowerName.includes("pool") || lowerName.includes("swim")) return "Pool";
  if (lowerName.includes("clubhouse") || lowerName.includes("club")) return "Clubhouse";
  if (lowerName.includes("game") || lowerName.includes("indoor")) return "Gaming";
  return "Recreation";
};

const getDynamicIcon = (name) => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes("gym") || lowerName.includes("fitness")) return <Dumbbell size={24} color="#014d33" />;
  if (lowerName.includes("pool") || lowerName.includes("swim")) return <Waves size={24} color="#014d33" />;
  if (lowerName.includes("clubhouse") || lowerName.includes("club")) return <HomeIcon size={24} color="#014d33" />;
  if (lowerName.includes("game") || lowerName.includes("indoor")) return <Gamepad2 size={24} color="#014d33" />;
  return <Sparkles size={24} color="#014d33" />;
};

const Amenities = () => {
  const navigate = useNavigate();
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Suggestion Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [suggestionForm, setSuggestionForm] = useState({
    name: "",
    email: "",
    phone: "",
    suggestion: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSuggestionChange = (e) => {
    setSuggestionForm({ ...suggestionForm, [e.target.name]: e.target.value });
  };

  const handleSuggestionSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/public/suggestions', suggestionForm);
      setSubmitSuccess(true);
      setTimeout(() => {
        setIsModalOpen(false);
        setSubmitSuccess(false);
        setSuggestionForm({ name: "", email: "", phone: "", suggestion: "" });
      }, 2000);
    } catch (error) {
      console.error("Failed to submit suggestion", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        const { data } = await api.get('/public/amenities');
        setAmenities(data);
      } catch (error) {
        console.error("Failed to fetch amenities", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAmenities();
  }, []);
  return (
    <div className={styles.facilitiesPage}>
      {/* Background Peacock Feather */}
      <svg
        className={styles.bgFeatherLeft}
        viewBox="0 0 200 500"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <ellipse
          cx="100"
          cy="60"
          rx="35"
          ry="55"
          stroke="#D9C39A"
          strokeWidth="1.5"
          fill="none"
        />
        <ellipse
          cx="100"
          cy="60"
          rx="18"
          ry="30"
          stroke="#B8CDBB"
          strokeWidth="1.5"
          fill="none"
        />
        <line
          x1="100"
          y1="115"
          x2="90"
          y2="480"
          stroke="#D9C39A"
          strokeWidth="1.5"
        />
      </svg>

      {/* Background Cityscape */}
      <svg
        className={styles.bgCityRight}
        viewBox="0 0 400 140"
        fill="none"
        stroke="#D9C39A"
        strokeWidth="1.5"
      >
        <rect x="230" y="60" width="50" height="70" fill="none" />
        <rect x="290" y="40" width="55" height="90" fill="none" />
        <rect x="355" y="70" width="35" height="60" fill="none" />
        <line x1="240" y1="75" x2="240" y2="120" strokeWidth="1" />
        <line x1="255" y1="75" x2="255" y2="120" strokeWidth="1" />
        <line x1="270" y1="75" x2="270" y2="120" strokeWidth="1" />
        <path d="M150 130a25 25 0 0 1 25-25 25 25 0 0 1 25 25" fill="none" />
        <path d="M190 130a20 20 0 0 1 20-20 20 20 0 0 1 20 20" fill="none" />
        <path d="M0 130h400" strokeWidth="1" />
      </svg>

      <div className={styles.contentWrapper}>
        {/* Header Section */}
        <div className={styles.headerSection}>
          <h1 className={styles.pageTitle}>Our Amenities</h1>
          <div className={styles.floralDivider}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--gold-accent, #c9a35f)"
              strokeWidth="1.2"
            >
              <path d="M12 3c-1.5 0-2.5 1.2-2.5 2.5S10.5 8 12 8s2.5-1.2 2.5-2.5S13.5 3 12 3z" />
              <path d="M12 21c-1.5 0-2.5-1.2-2.5-2.5S10.5 16 12 16s2.5 1.2 2.5 2.5S13.5 21 12 21z" />
              <path d="M3 12c0-1.5 1.2-2.5 2.5-2.5S8 10.5 8 12s-1.2 2.5-2.5 2.5S3 13.5 3 12z" />
              <path d="M21 12c0-1.5-1.2-2.5-2.5-2.5S16 10.5 16 12s1.2 2.5 2.5 2.5S21 13.5 21 12z" />
              <circle cx="12" cy="12" r="2" />
            </svg>
          </div>
          <p className={styles.leadText}>
            World-class amenities for a comfortable lifestyle.
          </p>
        </div>

        {/* Facilities Grid */}
        <div className={styles.facilitiesGrid}>
          {loading ? (
            <p>Loading amenities...</p>
          ) : amenities.length > 0 ? (
            amenities.map((facility) => (
              <div key={facility._id} className={styles.facilityCard}>
                <div className={styles.cardImageArea}>
                  <img
                    src={getAmenityImage(facility.name)}
                    alt={facility.name}
                    className={styles.facilityImg}
                  />
                  <div className={styles.imageBadge}>
                    <Waves size={14} /> Amenities
                  </div>
                </div>

                <div className={styles.cardContent}>
                  <div className={styles.cardHeader}>
                    <div className={styles.iconBadge}>
                      {getDynamicIcon(facility.name)}
                    </div>
                    <div className={styles.headerText}>
                      <h3 className={styles.facilityTitle}>{facility.name}</h3>
                      <span className={styles.categoryLabel}>{getCategoryText(facility.name)}</span>
                    </div>
                    <div className={styles.locationBadge}>
                      <MapPin size={14} /> On-Site
                    </div>
                  </div>

                  <p className={styles.facilityDesc}>
                    {facility.description && facility.description.length > 20 
                      ? facility.description 
                      : `Experience our premium ${facility.name}. Perfect for relaxation, leisure, and quality time with family & friends.`}
                  </p>

                  <div className={styles.cardFooter}>
                    <button className={styles.bookBtn} onClick={() => navigate('/resident/amenities')}>
                      Book Now <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '16px', gridColumn: '1 / -1', minHeight: '200px' }}>
              <XCircle size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', color: '#475569', marginBottom: '8px' }}>No Amenities Found</h3>
              <p style={{ color: '#64748b', textAlign: 'center', margin: 0, fontSize: '14px' }}>There are currently no amenities registered.</p>
            </div>
          )}
        </div>

        {/* Suggestion Banner */}
        <div className={styles.suggestionBanner}>
          <svg
            className={styles.bannerBgLeaf}
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M50 150C50 150 0 100 0 50C0 0 50 0 100 50C150 100 150 150 150 150H50Z"
              stroke="#D3E4D9"
              strokeWidth="2"
            />
            <path d="M50 150L100 50" stroke="#D3E4D9" strokeWidth="2" />
          </svg>
          <div className={styles.suggestionLeft}>
            <div className={styles.lightbulbIcon}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18h6"></path>
                <path d="M10 22h4"></path>
                <path d="M12 2v1"></path>
                <path d="M12 11v1"></path>
                <path d="M16 11l-4-4"></path>
                <path d="M8 11l4-4"></path>
              </svg>
            </div>
            <div className={styles.suggestionText}>
              <h3>Have a suggestion for a new amenity?</h3>
              <p>We are always looking to improve our community.</p>
            </div>
          </div>
          <button className={styles.primaryBtn} onClick={() => setIsModalOpen(true)}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
            Share Your Suggestion
          </button>
        </div>
      </div>

      {/* Suggestion Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Share a Suggestion</h2>
              <button className={styles.closeBtn} onClick={() => setIsModalOpen(false)}>
                <XCircle size={24} />
              </button>
            </div>
            {submitSuccess ? (
              <div className={styles.successMessage}>
                <Sparkles size={48} color="#0f5132" style={{ marginBottom: "16px" }} />
                <h3>Thank You!</h3>
                <p>Your suggestion has been submitted successfully to the admin team.</p>
              </div>
            ) : (
              <form onSubmit={handleSuggestionSubmit} className={styles.suggestionForm}>
                <div className={styles.formGroup}>
                  <label>Name (Optional)</label>
                  <input type="text" name="name" value={suggestionForm.name} onChange={handleSuggestionChange} placeholder="Enter your name" />
                </div>
                <div className={styles.formGroup}>
                  <label>Email (Optional)</label>
                  <input type="email" name="email" value={suggestionForm.email} onChange={handleSuggestionChange} placeholder="Enter your email" />
                </div>
                <div className={styles.formGroup}>
                  <label>Phone (Optional)</label>
                  <input type="tel" name="phone" value={suggestionForm.phone} onChange={handleSuggestionChange} placeholder="Enter your phone" />
                </div>
                <div className={styles.formGroup}>
                  <label>Suggestion *</label>
                  <textarea name="suggestion" value={suggestionForm.suggestion} onChange={handleSuggestionChange} placeholder="Write your suggestion here..." required rows="4" />
                </div>
                <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Submit Suggestion"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Amenities;
